import React from "react";
import PropTypes from "prop-types";
import { Icon, message, Modal, Select, Tag } from "antd";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import TvrSubsection from "./components/tvr-subsection";
import CallPlayer from "./components/call-player";
import CallTranscript from "./components/call-transcript";
import TranscriptAnalysis from "./components/transcript-analysis";
import CallLogsCard from "./components/call-logs-card";
import CallScheduleCard from "../call-schedule/call-schedule-card";
import LimitAssessmentCard from "./components/limit-assessment-card";
import { TVR_SUBSECTIONS, draftValueFromSuggestion } from "./tvr-config";
import {
    loadTvr,
    saveTvrFields,
    resetTvr,
    readPath,
    suggestionFor,
    shouldShowSuggestion,
    listPendingSuggestionFields,
    countPendingSuggestions,
    rejectTvrSuggestions
} from "./tvr-service";
import { SECTION_IDS } from "./constants";
import { confirmAndCall } from "./components/kyc-summary-table";
import { generateCreditBrief } from "services/case-service";

const { Option, OptGroup } = Select;

/**
 * Credit Tele Verification Report — the credit team's structured TVR as a single
 * clean form (one grouped grid, not a card per question). Each subsection is
 * read-only until unlocked with its pencil, and submits as one PATCH. The
 * verification-call recording playback and the transcript live here too.
 *
 * The TVR API is the single source of truth: every value on screen comes from
 * `/case/:id/tvr-details/` (the backend's `build_tvr_details` pre-fills an
 * untouched case) and every edit goes back to it. The client holds no pre-fill of
 * its own. Persistence goes through tvr-service.
 */
class UpdatedTvrSection extends React.Component {
    state = {
        tvr: null,
        // The form is always editable in the credit stages (see `canEdit`); there
        // is no manual edit toggle. `saving` covers the batch submit + PDF-regen
        // chain; `drafts` holds per-field pending values keyed by field.key across
        // the whole form, and Reset/Submit act on them.
        saving: false,
        drafts: {},
        loaded: false,
        showTranscript: false,
        callee: null,
        // Paths dismissed locally after Reject (backend keeps suggestion value).
        dismissedSuggestions: {},
        suggestionBusy: null
    };

    componentDidMount() {
        this._mounted = true;
        loadTvr(this.props.caseId).then(({ tvr }) => {
            if (this._mounted) {
                this.setState({ tvr, loaded: true });
            }
        });
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    /**
     * The value as STORED (ignoring any in-flight draft).
     *
     * The TVR API is the ONLY source: `build_tvr_details` on the backend pre-fills
     * an untouched case (constitution, nature of business, incorporation date,
     * anchor-associated-since) and the client adds nothing on top. The old
     * client-side pre-fill is gone, so the two can no longer disagree about what
     * an analyst is looking at.
     */
    storedValue = field => {
        const stored = readPath(this.state.tvr, field.path);
        return stored === undefined ? null : stored;
    };

    /**
     * Options for a field. Most carry their own static `options`; a field with
     * `optionsFrom: "applicants"` (TVR conducted with) is fed the case's
     * applicants — the same pool the call picker offers — so the two stay in step
     * as applicants are added or renamed. It is a `combo`, so these are
     * suggestions: the analyst can still type someone who is not on the case.
     */
    optionsFor = field => {
        if (field.optionsFrom !== "applicants") {
            return field.options;
        }
        return (this.props.applicants || [])
            .map(a =>
                [a.name, a.type].filter(Boolean).join(" · ")
            )
            .filter(Boolean);
    };

    // What the field renders: the draft while editing, else the stored value.
    valueFor = field =>
        Object.prototype.hasOwnProperty.call(this.state.drafts, field.key)
            ? this.state.drafts[field.key]
            : this.storedValue(field);

    // Compares a draft against the stored value. Both sides are normalised because
    // the controls emit strings ("42") while the API stores typed scalars (42) —
    // without this, re-picking the same value would look dirty. Lists are compared
    // structurally; String() would collapse every row to "[object Object]".
    isChanged = field => {
        if (!Object.prototype.hasOwnProperty.call(this.state.drafts, field.key)) {
            return false;
        }
        const norm = v => {
            if (v === null || v === undefined) {
                return "";
            }
            return typeof v === "object" ? JSON.stringify(v) : String(v);
        };
        return norm(this.state.drafts[field.key]) !== norm(this.storedValue(field));
    };

    // The accent means UNSAVED: it appears as soon as a draft diverges from the
    // stored value and clears on a successful submit (which wipes `drafts`).
    editedFor = field => this.isChanged(field);

    changedFields = section => section.fields.filter(this.isChanged);

    // Every field across every subsection that differs from its stored value.
    allChangedFields = () =>
        TVR_SUBSECTIONS.reduce(
            (acc, section) => acc.concat(this.changedFields(section)),
            []
        );

    // A field's current value (draft or stored) counts as empty. All TVR fields
    // are mandatory, so this drives the on-submit warning — it does NOT block.
    isEmptyValue = field => {
        const v = this.valueFor(field);
        if (field.type === "list") {
            return !Array.isArray(v) || v.length === 0;
        }
        return v === null || v === undefined || v === "";
    };

    // Every mandatory field still unfilled, in declaration order.
    emptyFields = () =>
        TVR_SUBSECTIONS.reduce(
            (acc, section) =>
                acc.concat(section.fields.filter(this.isEmptyValue)),
            []
        );

    // TVR is editable only in the credit stages (Credit Appraisal = 7,
    // Re-credit = 8), matching the CAM Assessment card's gate. Outside those the
    // form is read-only and both footer actions are disabled.
    canEdit = () => [7, 8].includes(Number(this.props.stageId));

    dismissedSet = () =>
        new Set(
            Object.keys(this.state.dismissedSuggestions).filter(
                path => this.state.dismissedSuggestions[path]
            )
        );

    // AI suggestion UI (banner, per-field cards, Accept/Reject) is credit-stage
    // only — same gate as form editing (stages 7 and 8).
    canShowSuggestions = () => this.canEdit();

    suggestionForField = field => suggestionFor(this.state.tvr, field);

    shouldShowSuggestionFor = field =>
        this.canShowSuggestions() &&
        shouldShowSuggestion(
            this.state.tvr,
            field,
            this.dismissedSet(),
            this.optionsFor
        );

    pendingSuggestionCount = () =>
        this.canShowSuggestions()
            ? countPendingSuggestions(
                  this.state.tvr,
                  this.dismissedSet(),
                  this.optionsFor
              )
            : 0;

    applySuggestionToDraft = field => {
        const suggestion = this.suggestionForField(field);
        const text = suggestion && suggestion.value;
        if (!text) {
            return false;
        }
        const next = draftValueFromSuggestion(field, this.valueFor(field), text);
        this.setState(prev => ({
            drafts: { ...prev.drafts, [field.key]: next },
            dismissedSuggestions: {
                ...prev.dismissedSuggestions,
                [field.path]: true
            }
        }));
        return true;
    };

    handleAcceptSuggestion = field => {
        if (!this.canEdit()) {
            return;
        }
        const applied = this.applySuggestionToDraft(field);
        message.success(
            applied
                ? "Suggestion filled in the form. Edit if needed, then Submit."
                : "No suggestion available for this field."
        );
    };

    handleRejectSuggestion = field => {
        if (!this.canEdit()) {
            return;
        }
        this.setState({ suggestionBusy: field.path });
        rejectTvrSuggestions(this.props.caseId, [field.path])
            .then(({ tvr }) => {
                if (!this._mounted) {
                    return;
                }
                this.setState(prev => ({
                    tvr,
                    suggestionBusy: null,
                    dismissedSuggestions: {
                        ...prev.dismissedSuggestions,
                        [field.path]: true
                    }
                }));
                message.info("Suggestion dismissed.");
            })
            .catch(() => {
                if (this._mounted) {
                    this.setState({ suggestionBusy: null });
                }
                message.error("Could not reject suggestion — please try again.");
            });
    };

    handleAcceptAllSuggestions = () => {
        if (!this.canEdit()) {
            return;
        }
        const pending = listPendingSuggestionFields(
            this.state.tvr,
            this.dismissedSet(),
            this.optionsFor
        );
        if (!pending.length) {
            return;
        }
        this.setState(prev => {
            const drafts = { ...prev.drafts };
            const dismissed = { ...prev.dismissedSuggestions };
            pending.forEach(field => {
                const suggestion = suggestionFor(prev.tvr, field);
                const text = suggestion && suggestion.value;
                if (!text) {
                    return;
                }
                const current = Object.prototype.hasOwnProperty.call(
                    drafts,
                    field.key
                )
                    ? drafts[field.key]
                    : this.storedValue(field);
                drafts[field.key] = draftValueFromSuggestion(
                    field,
                    current,
                    text
                );
                dismissed[field.path] = true;
            });
            return { drafts, dismissedSuggestions: dismissed };
        });
        message.success(
            `Filled ${pending.length} AI suggestion${
                pending.length === 1 ? "" : "s"
            } in the form. Edit if needed, then Submit.`
        );
    };

    handleChangeField = (field, value) => {
        if (!this.canEdit()) {
            return;
        }
        this.setState(prev => ({
            drafts: { ...prev.drafts, [field.key]: value }
        }));
    };

    // Reset — rebuild the TVR from source on the server, discarding every value
    // (including manually entered ones) and replacing them with freshly computed
    // pre-fills. Destructive and irreversible, so it confirms first, mirroring the
    // Bank Statement Analyser's "Rebuild".
    handleReset = () => {
        if (!this.canEdit()) {
            return;
        }
        Modal.confirm({
            title: "Reset TVR?",
            content:
                "This rebuilds the report from source and discards every manually entered value. This cannot be undone.",
            okText: "Reset",
            okType: "danger",
            cancelText: "Cancel",
            onOk: () => this.doReset()
        });
    };

    // Returns the promise so the confirm dialog shows its own loading state.
    doReset = () => {
        this.setState({ saving: true });
        return resetTvr(this.props.caseId)
            .then(({ tvr }) => {
                if (!this._mounted) return;
                this.setState({ tvr, drafts: {}, saving: false });
                if (this.props.onTvrSaved) {
                    this.props.onTvrSaved(tvr);
                }
                message.success("TVR reset to system values.");
            })
            .catch(() => {
                if (this._mounted) {
                    this.setState({ saving: false });
                }
                message.error("Could not reset the TVR — please try again.");
            });
    };

    // Submit — collects EVERY dirty field across the whole TVR into one PATCH,
    // then hits the credit-brief endpoint with regenerate=1 so the PDF reflects
    // the fresh TVR without a second manual click.
    handleSubmitAll = () => {
        if (!this.canEdit()) {
            return;
        }
        const changed = this.allChangedFields();
        if (!changed.length) {
            return;
        }
        // Empty mandatory fields are surfaced inline in the submit bar (see
        // render), not as a toast, and never block the submit.
        const changes = changed.map(field => ({
            field,
            value: this.state.drafts[field.key]
        }));
        this.setState({ saving: true });
        saveTvrFields(this.props.caseId, changes)
            .then(({ tvr }) => {
                if (!this._mounted) return;
                // TVR API is done → release the TVR buttons immediately. The
                // PDF regenerate and the AI refresh run independently and hold
                // THEIR OWN buttons (Credit Brief / Credit Decision), not this
                // one.
                this.setState({ tvr, saving: false, drafts: {} });
                // Push the fresh record into redux so the proceed checklist's
                // TVR-complete gate reflects this save without a full refetch.
                if (this.props.onTvrSaved) {
                    this.props.onTvrSaved(tvr);
                }
                message.success(
                    "TVR saved. Regenerating the credit brief PDF & refreshing AI…"
                );
                // Fire-and-inform: regenerate the PDF so downstream consumers
                // see fresh output. Failure to regen isn't fatal to the save.
                generateCreditBrief(this.props.caseId, true)
                    .then(() =>
                        message.success("Credit brief PDF regenerated.")
                    )
                    .catch(() =>
                        message.warning(
                            "TVR saved, but the PDF regenerate call failed — please regenerate from Credit Brief."
                        )
                    );
                // Kick off the AI refresh ON the Credit Decision section (via the
                // parent) so ITS "Refresh AI" button shows the loading/disabled
                // state — the TVR button is already free.
                if (this.props.onAiRefreshed) {
                    this.props.onAiRefreshed();
                }
            })
            .catch(() => {
                if (!this._mounted) return;
                // Stay in edit mode with drafts intact so the analyst can retry.
                this.setState({ saving: false });
                message.error(
                    "Could not save the TVR — your changes were not stored."
                );
            });
    };

    toggleTranscript = () =>
        this.setState(prev => ({ showTranscript: !prev.showTranscript }));

    setCallee = callee => this.setState({ callee });

    // The chosen callee — an applicant, or a Progcap contact (ASM / RM).
    selectedCallee() {
        const { applicants, contacts } = this.props;
        const pool = [...(applicants || []), ...(contacts || [])];
        return pool.find(c => c.id === this.state.callee) || null;
    }

    // Initiate the verification call to the chosen callee via Chitthi/Ozonetel
    // (same confirm + manual-call flow as the Applicant KYC call button), scoped
    // to the current user and case.
    startCall = () => {
        const callee = this.selectedCallee();
        if (!callee) {
            return;
        }
        const userId = this.props.user && this.props.user.id;
        confirmAndCall(callee, userId, this.props.caseId);
    };

    // Dropdown of applicants + the case's Progcap contacts (the ASM), plus a
    // Call button — lets the analyst place the TVR call, including the (common)
    // case where no call has happened yet.
    renderCallPicker() {
        const { applicants, contacts } = this.props;
        const hasApplicants = Boolean(applicants && applicants.length);
        const hasContacts = Boolean(contacts && contacts.length);
        if (!hasApplicants && !hasContacts) {
            return null;
        }
        const callee = this.selectedCallee();
        const canCall = Boolean(callee && callee.phone);
        return (
            <span className={"cc-tvr-call"}>
                <Select
                    size={"small"}
                    placeholder={"Call…"}
                    value={this.state.callee || undefined}
                    onChange={this.setCallee}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 170 }}
                >
                    {hasApplicants ? (
                        <OptGroup label={"Applicants"}>
                            {applicants.map(a => (
                                <Option key={a.id} value={a.id}>
                                    {a.name || "Applicant"}
                                    {a.type ? ` · ${a.type}` : ""}
                                </Option>
                            ))}
                        </OptGroup>
                    ) : null}
                    {hasContacts ? (
                        <OptGroup label={"Progcap team"}>
                            {contacts.map(c => (
                                <Option key={c.id} value={c.id}>
                                    {c.name}
                                    {c.type ? ` · ${c.type}` : ""}
                                </Option>
                            ))}
                        </OptGroup>
                    ) : null}
                </Select>
                <button
                    type={"button"}
                    className={"cc-tvr-callbtn"}
                    onClick={this.startCall}
                    disabled={!canCall}
                    title={
                        canCall
                            ? `Call ${callee.name}`
                            : callee
                                ? "No phone number on file"
                                : "Select someone to call"
                    }
                >
                    <Icon type={"phone"} /> Call
                </button>
            </span>
        );
    }

    renderCallControls() {
        const { call } = this.props;
        const picker = this.renderCallPicker();
        const hasRecording = Boolean(call && call.recordingUrl);
        const hasTranscript = Boolean(call && call.transcript);
        if (!picker && !hasRecording) {
            return null;
        }
        return (
            <div className={"cc-call-actions"}>
                {picker}
                {hasRecording ? (
                    <CallPlayer recordingUrl={call.recordingUrl} />
                ) : null}
                {hasTranscript ? (
                    <button
                        type={"button"}
                        className={
                            "cc-transcript-toggle" +
                            (this.state.showTranscript
                                ? " cc-transcript-toggle--on"
                                : "")
                        }
                        onClick={this.toggleTranscript}
                        aria-expanded={this.state.showTranscript}
                    >
                        <Icon type={"file-text"} />
                        <span>
                            {this.state.showTranscript
                                ? "Hide transcript"
                                : "View transcript"}
                        </span>
                    </button>
                ) : null}
            </div>
        );
    }

    renderTranscript() {
        const { call } = this.props;
        if (!call || !call.transcript || !this.state.showTranscript) {
            return null;
        }
        return (
            <React.Fragment>
                {call.transcriptAnalysis ? (
                    <CardBlock
                        icon={"audit"}
                        title={"Transcript Analysis"}
                        subtitle={
                            "AI summary, tone and what went well / concerns / red flags"
                        }
                    >
                        <TranscriptAnalysis analysis={call.transcriptAnalysis} />
                    </CardBlock>
                ) : null}
                <CardBlock icon={"message"} title={"Call Transcript"}>
                    <CallTranscript transcript={call.transcript} />
                </CardBlock>
            </React.Fragment>
        );
    }

    // Top-right header actions: just the Call controls. There is no edit toggle —
    // the form is editable whenever the stage allows (see `canEdit`); Reset and
    // Submit live at the bottom of the form.
    renderHeaderActions() {
        return this.renderCallControls();
    }

    /**
     * Non-field content that belongs to a subsection. Limit Assessment is the SAME
     * component (not a copy) as its own section, rendered here in its "tvr" variant
     * — every headline metric shown as a disabled text field so it reads as part of
     * the TVR form. It is display-only here (the editable Proposed Limit lives in
     * the standalone Limit Assessment section); it does not round-trip through the
     * form's PATCH.
     *
     * (Brand Mix used to render here too, as the full interactive GST-pull table.
     * It is now a one-line text field — `brand_mix_summary` — pre-filled from that
     * same pull by the backend, so the interactive table stays in Company Detail
     * and the TVR just carries the digest.)
     */
    renderSectionFooter(section) {
        if (section.id === "financial_performance") {
            return (
                <LimitAssessmentCard
                    variant={"tvr"}
                    topMetrics={this.props.topMetrics}
                    proposedLimit={this.props.proposedLimit}
                    exposureData={this.props.exposureData}
                />
            );
        }
        return null;
    }

    render() {
        const { innerRef, expanded, onToggle } = this.props;
        const { saving } = this.state;
        const canEdit = this.canEdit();
        const showSuggestions = this.canShowSuggestions();
        const dirtyCount = this.allChangedFields().length;
        const pendingSuggestions = this.pendingSuggestionCount();
        const suggestionMeta =
            this.state.tvr &&
            this.state.tvr.suggestion_meta &&
            typeof this.state.tvr.suggestion_meta === "object"
                ? this.state.tvr.suggestion_meta
                : null;
        // Still-empty mandatory fields — surfaced inline under the change hint in
        // the submit bar (no toast). Does not block submit.
        const empties = this.emptyFields();
        // Reset is a server rebuild, valid whenever the stage allows — even with
        // no local edits. Submit additionally needs pending changes to send.
        const resetDisabled = !canEdit || saving;
        const submitDisabled = resetDisabled || dirtyCount === 0;
        // Call-schedule recipient — the case's main borrower (from the same
        // applicants list this tab already renders). Fallback: when no borrower
        // is flagged but the case has exactly one applicant, use that applicant.
        const applicantList = this.props.applicants || [];
        const mainBorrower =
            applicantList.find(a => a && a.isMainBorrower) ||
            (applicantList.length === 1 ? applicantList[0] : null);
        return (
            <SectionBlock
                id={SECTION_IDS.UPDATED_TVR}
                innerRef={innerRef}
                title={"Credit Tele Verification Report"}
                icon={"snippets"}
                expanded={expanded}
                onToggle={onToggle}
                extra={this.renderHeaderActions()}
            >
                {this.renderTranscript()}
                {showSuggestions && suggestionMeta && suggestionMeta.source ? (
                    <div className={"cc-tvr-suggestion-meta"}>
                        <Icon type={"bulb"} className={"cc-tvr-suggestion-meta__bulb"} />
                        <span className={"cc-tvr-suggestion-meta__text"}>
                            AI suggestions from Teams call
                            {suggestionMeta.call_id
                                ? ` #${suggestionMeta.call_id}`
                                : ""}
                            {pendingSuggestions > 0
                                ? ` — ${pendingSuggestions} pending`
                                : ""}
                        </span>
                        {canEdit && pendingSuggestions > 0 ? (
                            <button
                                type={"button"}
                                className={"cc-tvr-suggestion-meta__accept-all"}
                                onClick={this.handleAcceptAllSuggestions}
                                disabled={
                                    saving ||
                                    this.state.suggestionBusy === "all"
                                }
                                title={"Accept all pending suggestions"}
                            >
                                <Icon
                                    type={
                                        this.state.suggestionBusy === "all"
                                            ? "loading"
                                            : "check"
                                    }
                                />
                                <span>Accept all</span>
                            </button>
                        ) : null}
                    </div>
                ) : null}
                <div className={"cc-tvr-form"}>
                    {TVR_SUBSECTIONS.map(section => (
                        <TvrSubsection
                            key={section.id}
                            section={section}
                            editing={canEdit}
                            saving={saving}
                            valueFor={this.valueFor}
                            optionsFor={this.optionsFor}
                            editedFor={this.editedFor}
                            onChangeField={this.handleChangeField}
                            suggestionFor={
                                showSuggestions ? this.suggestionForField : null
                            }
                            shouldShowSuggestion={
                                showSuggestions
                                    ? this.shouldShowSuggestionFor
                                    : () => false
                            }
                            suggestionBusy={
                                showSuggestions
                                    ? this.state.suggestionBusy
                                    : null
                            }
                            onAcceptSuggestion={
                                showSuggestions
                                    ? this.handleAcceptSuggestion
                                    : undefined
                            }
                            onRejectSuggestion={
                                showSuggestions
                                    ? this.handleRejectSuggestion
                                    : undefined
                            }
                            footer={this.renderSectionFooter(section)}
                        />
                    ))}
                </div>
                {/* Reset + Submit at the bottom of the whole TVR. Always shown;
                    both are disabled outside the credit stages (see `canEdit`) or
                    when there is nothing pending. Submit persists the batch and
                    then triggers a credit-brief PDF regenerate; Reset discards the
                    pending edits. */}
                <div className={"cc-tvr-final-bar"}>
                    {/* Row 1: status hint on the left, actions on the right —
                        the buttons keep their own row so their labels never
                        wrap. */}
                    <div className={"cc-tvr-final-bar__row"}>
                        <span className={"cc-tvr-final-bar__hint"}>
                            {!canEdit
                                ? "Editable only at the credit stage"
                                : pendingSuggestions > 0
                                    ? `${pendingSuggestions} AI suggestion${
                                          pendingSuggestions === 1 ? "" : "s"
                                      } pending`
                                    : dirtyCount > 0
                                        ? `${dirtyCount} unsaved field${
                                              dirtyCount === 1 ? "" : "s"
                                          }`
                                        : "No changes yet"}
                        </span>
                        {canEdit && pendingSuggestions > 0 ? (
                            <button
                                type={"button"}
                                className={"cc-tvr-suggestion-meta__accept-all cc-tvr-suggestion-meta__accept-all--compact"}
                                onClick={this.handleAcceptAllSuggestions}
                                disabled={
                                    saving ||
                                    this.state.suggestionBusy === "all"
                                }
                                title={"Apply every pending AI suggestion"}
                            >
                                <Icon
                                    type={
                                        this.state.suggestionBusy === "all"
                                            ? "loading"
                                            : "check-circle"
                                    }
                                />
                                <span>Accept all</span>
                            </button>
                        ) : null}
                        {canEdit ? (
                            <button
                                type={"button"}
                                className={"cc-tvr-cancelbtn"}
                                onClick={this.handleReset}
                                disabled={resetDisabled}
                                title={
                                    "Rebuild the TVR from source (discards manual edits)"
                                }
                            >
                                <Icon type={"undo"} />
                                <span>Reset</span>
                            </button>
                        ) : null}
                        <button
                            type={"button"}
                            className={"cc-tvr-submitbtn cc-tvr-submitbtn--final"}
                            onClick={this.handleSubmitAll}
                            disabled={submitDisabled}
                            title={
                                !canEdit
                                    ? "TVR can only be edited at the credit stage"
                                    : dirtyCount === 0
                                        ? "No changes to save"
                                        : "Save all TVR changes and regenerate the credit brief PDF"
                            }
                        >
                            <Icon type={saving ? "loading" : "check"} />
                            <span>
                                {saving ? "Saving…" : "Submit & regenerate PDF"}
                            </span>
                        </button>
                    </div>
                    {/* Row 2: empty mandatory fields as compact chips, full width
                        below the actions. */}
                    {canEdit && empties.length > 0 ? (
                        <div className={"cc-tvr-final-bar__warn"}>
                            <span className={"cc-tvr-final-bar__warn-head"}>
                                <Icon type={"exclamation-circle"} />{" "}
                                {`${empties.length} mandatory field${
                                    empties.length === 1 ? "" : "s"
                                } empty:`}
                            </span>
                            {empties.map(f => (
                                <Tag key={f.key} className={"cc-tvr-warn-chip"}>
                                    {f.label}
                                </Tag>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className={"cc-tvr-callschedule"} style={{ marginBottom: 16 }}>
                    <CallScheduleCard
                        caseId={this.props.caseId}
                        collapsible
                        defaultCollapsed
                        recipientName={mainBorrower && mainBorrower.name}
                        recipientPhone={mainBorrower && mainBorrower.phone}
                        recipientEmail={mainBorrower && mainBorrower.email}
                    />
                </div>
                <div className={"cc-tvr-calllogs"}>
                    <CallLogsCard caseId={this.props.caseId} />
                </div>
            </SectionBlock>
        );
    }
}

UpdatedTvrSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Case stage — the form is editable only for the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    user: PropTypes.object,
    call: PropTypes.object,
    applicants: PropTypes.array,
    // Progcap-side call contacts (ASM) — [{ id, type, name, phone }]
    contacts: PropTypes.array,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    // Called with the saved TVR record after a successful submit, so the parent
    // can sync it into redux (proceed-checklist TVR-complete gate).
    onTvrSaved: PropTypes.func,
    // Called after the post-submit AI refresh completes so the parent can
    // reload the Credit Decision card with the freshly re-run result.
    onAiRefreshed: PropTypes.func,
    // Limit Assessment — forwarded straight to the shared LimitAssessmentCard,
    // same props the standalone Limit Assessment section passes it.
    topMetrics: PropTypes.array,
    proposedLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    exposureData: PropTypes.object,
    isRiskApprover: PropTypes.bool,
    isUserAllowedToEdit: PropTypes.bool,
    recreditViewOnlyPermission: PropTypes.bool,
    onProposedLimitChange: PropTypes.func
};

export default UpdatedTvrSection;
